import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock } from "lucide-react";
import { useState } from "react";
import { CommissionStateBadge, formatCents, LoadingPanel } from "./shared";
import type { CommissionRow } from "./types";

type StatusMessage = { type: "success" | "error"; text: string };

export function FirstCommissionQueuePage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["affiliate-first-hold-queue"],
    queryFn: () => api.affiliates.listFirstHoldQueue(),
  });

  const rows: CommissionRow[] = data?.commissions ?? [];

  const releaseMutation = useMutation({
    mutationFn: (id: string) => api.affiliates.releaseFirstHold(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate-first-hold-queue"] });
      showStatus("success", "First-commission hold released");
    },
    onError: (err: Error) => showStatus("error", `Release failed: ${err.message}`),
  });

  const handleRelease = async (row: CommissionRow) => {
    const ok = await confirm({
      title: `Release first-commission hold?`,
      description: `Clears the fraud hold on this ${formatCents(row.amountCents, row.currency)} commission. The refund-window hold (dueAt) still applies.`,
      confirmLabel: "Release hold",
      confirmColor: "amber",
    });
    if (ok) releaseMutation.mutate(row.id);
  };

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="First-Commission Hold Queue"
        subtitle="Each affiliate's first commission is held for manual review"
      />

      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {isLoading ? (
        <LoadingPanel />
      ) : rows.length === 0 ? (
        <div className="py-16 text-center">
          <Clock className="mx-auto h-12 w-12 text-zinc-400" />
          <Text className="mt-2 text-zinc-500">No held first commissions</Text>
        </div>
      ) : (
        <Table dense striped>
          <TableHead>
            <TableRow>
              <TableHeader>Affiliate</TableHeader>
              <TableHeader>State</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Invoice</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader className="text-right">Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="text-sm">
                  {c.affiliateEmail || <span className="font-mono text-xs">{c.affiliateId}</span>}
                </TableCell>
                <TableCell>
                  <CommissionStateBadge state={c.state} />
                </TableCell>
                <TableCell className="font-medium">
                  {formatCents(c.amountCents, c.currency)}
                </TableCell>
                <TableCell className="font-mono text-xs">{c.stripeInvoiceId}</TableCell>
                <TableCell className="text-sm text-zinc-500">{formatDate(c.createdAt)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    color="amber"
                    disabled={releaseMutation.isPending}
                    onClick={() => handleRelease(c)}
                  >
                    Release
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
